<template>
  <div class="animated fadeIn">
    <div class="row">
      <div class="col">
        <b-overlay :show="isExporting" no-center blur="">
          <template #overlay>
            <div class="position-absolute text-center"
              style="top: 350px; left: 50%; transform: translateX(-50%) translateY(-50%);">
              <ve-progress
                :progress="downloadProgress.percentCompleted"
                :loading="!downloadProgress.percentCompleted"
                :determinate="!downloadProgress.totalFileSize"
                :legend="downloadProgress.processedFileSize"
                :legend-formatter="({value}) => $n(downloadProgress.processedFileSize / 1024 / 1024, { minimumFractionDigits: 1, maximumFractionDigits: 1 })"
                animation="default 0 0"
                fontSize="1.25rem"
                :emptyColor="downloadProgress.emptyColor"
                :color="downloadProgress.color">
                <span slot="legend">
                   MB <span v-if="downloadProgress.totalFileSize">/ {{ $n(downloadProgress.totalFileSize / 1024 / 1024, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) }} MB</span>
                </span>
                <span slot="legend-caption">
                  {{ downloadProgress.action }}
                </span>
              </ve-progress>
              <h3 class="mt-3">{{ $t('Webportal.Progress.Description') }}</h3>
            </div>
          </template>

          <b-card>
            <template v-slot:header>
              <survey-filter  v-model="surveyFilter"
                              :disabled="isExporting"
                              @update:modelValue="loadData" />

              <b-row class="media-explorer-toolbar align-items-center">
                <b-form-group class="mb-3 col-md-4" style="display: flex;">
                  <treeselect class="treeselect_upload"
                              ref="uploadTreeselect"
                              v-model="uploadValue"
                              :auto-load-root-options="false"
                              :placeholder="$t('Webportal.UploadFilter.Placeholder')"
                              :options="uploadItems"
                              :disabled="noSurveySelected || isExporting"
                              :disableFuzzyMatching="true"
                              :multiple="true"
                              :showCount="true"
                              showCountOf="LEAF_DESCENDANTS"
                              :flat="true"
                              :defaultExpandLevel="1"
                              :disable-branch-nodes="true"
                              :limit="4"
                              :limit-text="limitText"
                              @close="loadData"
                              @input="filterInput">
                    <template v-slot:before-list>
                      <div class="ps-1">
                        <small>
                          {{ $t('Webportal.UploadFilter.Select') }}:
                          <b-link @click="selectUploadQuestions(UploadQuestionType.All)"
                            :disabled="isUploadQuestionTypeDisabled(UploadQuestionType.All)">
                            {{ $t('Webportal.UploadFilter.All') }}
                          </b-link> /
                          <b-link @click="selectUploadQuestions(UploadQuestionType.Standard)"
                            :disabled="isUploadQuestionTypeDisabled(UploadQuestionType.Standard)">
                            {{ $t('Webportal.UploadFilter.Standard') }}
                          </b-link> /
                          <b-link @click="selectUploadQuestions(UploadQuestionType.Picture)"
                            :disabled="isUploadQuestionTypeDisabled(UploadQuestionType.Picture)">
                            {{ $t('Webportal.UploadFilter.Picture') }}
                          </b-link> /
                          <b-link @click="selectUploadQuestions(UploadQuestionType.Video)"
                            :disabled="isUploadQuestionTypeDisabled(UploadQuestionType.Video)">
                            {{ $t('Webportal.UploadFilter.Video') }}
                          </b-link>
                        </small>
                      </div>
                    </template>
                    <template v-slot:option-label="{ node }">
                      <font-awesome-icon v-if="node.raw.IsPictureUploadQuestion" icon="image" />
                      <font-awesome-icon v-if="node.raw.IsVideoUploadQuestion" icon="video" />
                      {{ node.label }}
                    </template>
                  </treeselect>
                  <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.DownloadUploadFiles')">
                    <b-button class="treeselect_upload_btn"
                              variant="primary"
                              :disabled="isExporting || downloadDisabled"
                              @click="downloadUploadFiles()">
                      <!-- {{ $t('Webportal.Download') }} -->
                      <font-awesome-icon icon="download" />
                    </b-button>
                  </span>
                </b-form-group>

                <b-form-group class="mb-3 col-md-4" style="display: flex;" v-if="hasSurveyInstanceProofs()">
                  <treeselect class="treeselect_upload"
                              ref="uploadTreeselect"
                              v-model="uploadProofValue"
                              :auto-load-root-options="false"
                              :placeholder="$t('Webportal.UploadProofFilter.Placeholder')"
                              :options="uploadProofItems"
                              :disabled="noSurveySelected || isExporting"
                              :disableFuzzyMatching="true"
                              :multiple="true"
                              :showCount="true"
                              showCountOf="LEAF_DESCENDANTS"
                              :flat="true"
                              :defaultExpandLevel="1"
                              :disable-branch-nodes="true"
                              :limit="4"
                              :limit-text="limitText"
                              @close="loadData"
                              @input="filterInput">
                    <template v-slot:before-list>
                      <div class="ps-1">
                        <small>
                          {{ $t('Webportal.UploadFilter.Select') }}:
                          <b-link @click="selectUploadProofs(UploadQuestionType.All)"
                            :disabled="isUploadProofTypeDisabled(UploadQuestionType.All)">
                            {{ $t('Webportal.UploadFilter.All') }}
                          </b-link> /
                          <b-link @click="selectUploadProofs(UploadQuestionType.Standard)"
                            :disabled="isUploadProofTypeDisabled(UploadQuestionType.Standard)">
                            {{ $t('Webportal.UploadFilter.Standard') }}
                          </b-link> /
                          <b-link @click="selectUploadProofs(UploadQuestionType.Picture)"
                            :disabled="isUploadProofTypeDisabled(UploadQuestionType.Picture)">
                            {{ $t('Webportal.UploadFilter.Picture') }}
                          </b-link> /
                          <b-link @click="selectUploadProofs(UploadQuestionType.Video)"
                            :disabled="isUploadProofTypeDisabled(UploadQuestionType.Video)">
                            {{ $t('Webportal.UploadFilter.Video') }}
                          </b-link>
                        </small>
                      </div>
                    </template>
                    <template v-slot:option-label="{ node }">
                      <font-awesome-icon v-if="node.raw.IsPictureUploadQuestion" icon="image" />
                      <font-awesome-icon v-if="node.raw.IsVideoUploadQuestion" icon="video" />
                      {{ node.label }}
                    </template>
                  </treeselect>
                  <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.DownloadUploadFiles')">
                    <b-button class="treeselect_upload_btn"
                              variant="primary"
                              :disabled="isExporting || downloadProofDisabled"
                              @click="downloadUploadProofFiles()">
                      <!-- {{ $t('Webportal.Download') }} -->
                      <font-awesome-icon icon="download" />
                    </b-button>
                  </span>
                </b-form-group>

                <b-form-group class="mb-3 col-md-auto ms-auto pl-2">
                  <pagination ref="pagination"
                    v-model="selectedPage"
                    :totalCount="totalCount"
                    :selectedPageSize="selectedPageSize"
                    @update:selectedPageSize="selectedPageSize = $event"
                    :disabled="isExporting"
                    :showAllRowsToolTip="$t('Webportal.ShowAllChecks')"
                    @input="selectedPage = $event; loadDataInternal()" />
                </b-form-group>
              </b-row>
            </template>

            <div v-bind:class="{ spinner: isLoading }" style="min-height:300px;">
              <!-- Important: use @click.stop and @click.native.stop for action items inside table
                  https://bootstrap-vue.js.org/docs/components/table/ -->
              <b-table v-if="!this.noSurveySelected"
                      small hover responsive :fixed="browserIsIE"
                      :foot-clone="items && items.length >= 5"
                      :items="items"
                      :fields="fields"
                      no-local-sorting
                      @sorted="sortChanged"
                      @row-clicked="rowClicked">
                <template v-slot:head(Select)>
                  <b-form-checkbox v-model="toggleSelectionChecked"
                                  :indeterminate="toggleSelectionIndeterminate"
                                  style="min-height:1.0rem"
                                  @click.stop
                                  @change="toggleSelection"
                                  v-b-tooltip.hover.nofade="toolTipDelay"
                                  :title="$t('Webportal.SelectAll')" />
                </template>

                <template v-slot:top-row>
                  <td></td>
                  <td></td>
                  <td>
                    <search-input :value="columnFilter.SurveyInstance.ExternalSurveyInstanceId"
                                  @input="columnFilter.SurveyInstance.ExternalSurveyInstanceId = $event"
                                  @change="loadData"
                                  small :placeholder="$t('Webportal.SearchTextFilter.Placeholder')" />
                  </td>
                  <td>
                    <search-input :value="columnFilter.ClientLocation.ClientLocationIdOnClientSite"
                                  @input="columnFilter.ClientLocation.ClientLocationIdOnClientSite = $event"
                                  @change="loadData"
                                  small :placeholder="$t('Webportal.SearchTextFilter.Placeholder')" />
                  </td>
                  <td>
                    <search-input :value="columnFilter.ClientLocation.FullAddress"
                                  @input="columnFilter.ClientLocation.FullAddress = $event"
                                  @change="loadData"
                                  small :placeholder="$t('Webportal.SearchTextFilter.Placeholder')" />
                  </td>
                  <td v-if="isCalendarWeekFieldVisible">
                    <search-input :value="columnFilter.SurveyInstance.CalendarWeek"
                                  @input="columnFilter.SurveyInstance.CalendarWeek = $event"
                                  @change="loadData"
                                  small :placeholder="$t('Webportal.SearchTextFilter.Placeholder')" />
                  </td>
                  <td>
                    <search-select  :value="columnFilter.UploadCount"
                                    @input="columnFilter.UploadCount = $event"
                                    @change="loadData"
                                    :options="uploadCountOptions"
                                    small :placeholder="$t('Webportal.SearchValueFilter.Placeholder')" />
                  </td>
                  <td>
                    <search-input :value="columnFilter.SurveyInstance.Result"
                                  @input="columnFilter.SurveyInstance.Result = $event"
                                  @change="loadData"
                                  small :placeholder="$t('Webportal.SearchTextFilter.Placeholder')" />
                  </td>
                  <td v-if="isStatusFieldVisible" colspan="2">
                    <search-select  :value="columnFilter.SurveyInstance.SurveyInstanceStatusId"
                                    @input="columnFilter.SurveyInstance.SurveyInstanceStatusId = $event"
                                    @change="loadData"
                                    :options="getSurveyInstanceStatusOptions"
                                    small :placeholder="$t('Webportal.SearchValueFilter.Placeholder')" />
                  </td>
                  <td v-else></td>
                </template>

                <template v-slot:cell(Select)="data">
                  <b-form-checkbox v-model="data.item.Selected"
                                  @click.stop
                                  @change="updateToggleSelection" />
                </template>
                <template v-slot:[`cell(Survey.Title)`]="data">
                  {{ data.value }}
                </template>
                <template v-slot:[`cell(SurveyInstance.ExternalSurveyInstanceId)`]="data">
                  {{ data.value }}
                </template>
                <template v-slot:[`cell(ClientLocation.ClientLocationIdOnClientSite)`]="data">
                  {{ data.value }}
                </template>
                <template v-slot:cell(ClientLocation)="data">
                  {{ data.value.Name }}<br>
                  {{ data.value.Address }}<br>
                  {{ data.value.City }}, {{ data.value.PostalCode }}
                </template>
                <template v-slot:[`cell(SurveyInstance.TimeStampSurveyStart)`]="data">
                  <template v-if="isMsmUser || data.item.ShowCheckDate || data.item.ShowCheckTime">
                    {{ calendarWeekAndDateFormatted(data.item) }}
                  </template>
                </template>
                <template v-slot:[`cell(SurveyInstance.Result)`]="data">
                  {{ (data.value != null) ? $n(data.value) + '%' : '' }}
                </template>
                <template v-slot:[`cell(SurveyInstance.SurveyInstanceStatusId)`]="data">
                  <template v-if="isMsmUser || data.item.ShowCheckStatus">
                    <span v-b-tooltip.hover.nofade="toolTipDelay" :title="getSurveyInstanceStatusText(data.value)">
                      <font-awesome-icon :icon="getSurveyInstanceStatusIcon(data.value)" fixed-width />
                    </span>
                  </template>
                </template>
                <template v-slot:cell(Action)="data">
                  <b-button v-if="data.item.HasVideoUpload"
                            pill variant="primary" size="sm" @click="showPreview(data.item.SurveyInstance.SurveyInstanceId)"
                            v-b-tooltip.hover.nofade="toolTipDelay"
                            :title="$t('Webportal.ShowVideo')"
                            @click.stop>
                    <font-awesome-icon icon="video" />
                  </b-button>
                </template>
              </b-table>
            </div>
          </b-card>
        </b-overlay>
      </div>

      <div class="col-5 p-0" v-bind:class="{ 'd-none': !isPreviewVisible }">
        <div class="pdf-viewer card col-5 p-0">
          <div>
            <button type="button" class="btn-close" aria-label="Close" @click="closePreview()"
                    v-b-tooltip.hover.nofade="toolTipDelay"
                    :title="$t('Webportal.ClosePreview')">
            </button>
          </div>
          <div class="card-body pt-0" v-bind:class="{ spinner: isPreviewLoading }">
            <video id="video" class="w-100" ref="videoControl" controls>
              <source :src="previewUrl" type="video/mp4" />
              <p>OOPS! It looks like your browser doesn't support HTML5 videos. Please use the latest version of a modern browser.</p>
            </video>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import AppConfig from '@/mixins/AppConfig'
import DownloadFileMixin from '@/mixins/DownloadFileMixin'
import SurveyInstanceMixin from '@/mixins/SurveyInstanceMixin';

import SurveyFilter from '@/components/SurveyFilter-APP_TARGET';
import SearchInput from '@/components/SearchInput'
import SearchSelect from '@/components/SearchSelect'
import Pagination from '@/components/Pagination.vue'

import EventBus from '@/services/EventBus.js';
import SurveysService from '@/services/SurveysService'
import MediaService from '@/services/MediaService'
import AuthenticationService from '@/services/AuthenticationService'

import VueEllipseProgress from 'vue-ellipse-progress';

import CustomFilter from '@/plugins/CustomFilter';
import { useToast } from 'bootstrap-vue-next';

const UploadQuestionType = {
  All: 0,
  Standard: 1,
  Picture: 2,
  Video: 3,
}

export default {
  name: "MediaExplorer",

  components: { SurveyFilter, SearchInput, SearchSelect, Pagination,VueEllipseProgress },

  mixins: [ AppConfig, DownloadFileMixin, SurveyInstanceMixin ],

  setup() {
    const { show } = useToast();
    return { showToast: show };
  },

  data () {
    return {
      isLoading: false,                     // Determines if data loading is currently in progress

      UploadQuestionType: UploadQuestionType,

      // Filter properties
      groupUploadQuestions: APP_TARGET === 'porschesar' ? "QuestionNumber" : null,
      uploadItems: [],
      uploadValue: [],
      uploadProofItems: [],
      uploadProofValue: [],

      columnFilter: {                       // Filter values for each column
        Survey: { Title: null },
        SurveyInstance : {
          ExternalSurveyInstanceId: null,
          CalendarWeek: null,
          Result: null,
          SurveyInstanceStatusId: null },
        ClientLocation: {
          ClientLocationIdOnClientSite: null,
          FullAddress: null },
        UploadCount: APP_TARGET === 'porschesar' ? ">0" : null,
      },

      sortBy: null,                         // Sorting column
      sortDescending: false,                // Sorting order

      surveyFilter: [],
      currentSurveyValue: null,             // Current selected structure item for change detection
      currentFilter: null,                  // Current filter object for change detection

      isExporting: false,

      // Paging properties
      selectedPage: null,                   // Number of the page, that is currently visible
      selectedPageSize: null,               // Number of items on one page of the table
      totalCount: 0,                        // Number of total items

      // Table properties
      items: [],

      toggleSelectionChecked: false,
      toggleSelectionIndeterminate: false,

      previewUrl: null,
      isPreviewLoading: false,
      isPreviewVisible: false,
      previewCancelToken: null,
      surveyInstancesCancelToken: null,

      downloadProgress: {
        action: 'init',
        percentCompleted: 0,
        processedFileSize: null,
        totalFileSize: undefined,

      },
    }
  },

  computed: {
    isCalendarWeekFieldVisible() {
      return this.isMsmUser || this.items?.some(i => i.ShowCheckDate || i.ShowCheckTime) || this.columnFilter.SurveyInstance.CalendarWeek != null || this.sortBy === 'SurveyInstance.TimeStampSurveyStart';
    },

    isStatusFieldVisible() {
      return this.isMsmUser || this.items?.some(i => i.ShowCheckStatus) || this.columnFilter.SurveyInstance.SurveyInstanceStatusId != null || this.sortBy === 'SurveyInstance.SurveyInstanceStatusId';
    },

    fields() {
      return [
        { key: 'Select', thStyle: 'width:2em' },
        { key: 'Survey.Title', label: this.$t('Webportal.Survey'), thStyle: '', sortable: true },
        { key: 'SurveyInstance.ExternalSurveyInstanceId', label: this.$t('Webportal.SurveyInstanceId'), thStyle: 'width:7em', sortable: true },
        { key: 'ClientLocation.ClientLocationIdOnClientSite', label: this.$t('Webportal.LocationId'), thStyle: 'width:8em', sortable: true },
        { key: 'ClientLocation', label: this.$t('Webportal.LocationAddress'), thStyle: '', sortable: true },
        ... this.isCalendarWeekFieldVisible ? [{ key: 'SurveyInstance.TimeStampSurveyStart', label: this.$t('Webportal.CalendarWeek'), class: 'text-center', thStyle: 'width:11em', sortable: true }] : [],
        { key: 'UploadCount', label: this.$t('Webportal.UploadCount'), class: 'text-end', thStyle: 'width:9em', sortable: true },
        { key: 'SurveyInstance.Result', label: this.$t('Webportal.Result'), class: 'text-end', thStyle: 'width:6em', sortable: true },
        ... this.isStatusFieldVisible ? [{ key: 'SurveyInstance.SurveyInstanceStatusId', label: this.$t('Webportal.Status'), thStyle: 'width:5em', thClass: 'text-center', tdClass: 'text-center', sortable: true }] : [],
        { key: 'Action', label: '', thStyle: 'width:3em' }
      ];
    },

    filter: function() {
      // Creates a new object without empty values from source
      const isNotEmptyPredicate = o => !_.isNil(o);
      const pickByDeep = (obj, fn) =>
        _.pickBy( // Remove on root level
          _.mapValues(obj, (val, key) =>
            !_.isObjectLike(val) ? val : // Take plain values
              !_.isEmpty(_.pickBy(val, fn)) ? _.pickBy(val, fn) : null) // Map values of nested object
          , fn)

      return {
        surveyIds: this.surveyFilter.computedSurveyIds,
        waveIds: this.surveyFilter.waveIds,
        scenarioIds: this.surveyFilter.scenarioIds,
        dateFrom: this.surveyFilter.dateFrom,
        dateTo: this.surveyFilter.dateTo,
        datePeriods: this.surveyFilter.datePeriods,
        propertyIds: this.surveyFilter.propertyIds,
        locationIds: this.surveyFilter.clientLocationIds,
        searchText: this.searchText,
        columnFilter: pickByDeep(this.columnFilter, isNotEmptyPredicate),
        withUploads: true,
        uploadQuestionIds: this.uploadValue,
        uploadProofIds: this.uploadProofValue,
        sortBy: this.sortBy,
        sortDescending: this.sortDescending
      };
    },

    options: function() {
      let result = {
        page: this.selectedPage,
        pageSize: this.selectedPageSize,
        propertyUseLogicalAnd: this.surveyFilter.propertyUseLogicalAnd
      }

      return result;
    },

    noSurveySelected: function () {
      return _.isEmpty(this.surveyFilter.computedSurveyIds);
    },

    selectedSurveyInstanceIds: function () {
      return _.map(this.items.filter(item => item.Selected), 'SurveyInstance.SurveyInstanceId');
    },

    downloadProofDisabled: function () {
      return _.isEmpty(this.items) || _.isEmpty(this.uploadProofValue) ||_.isEmpty(this.selectedSurveyInstanceIds);
    },

    downloadDisabled: function () {
      return _.isEmpty(this.items) || _.isEmpty(this.uploadValue) ||_.isEmpty(this.selectedSurveyInstanceIds);
    },

    isMsmUser: function() {
      return AuthenticationService.isMSMUser;
    },

    uploadCountOptions: function() {
      return [
          { value: '>0', text: this.$t('Webportal.MoreThan', { value: 0 })},
          { value: '1', text: '1' },
          { value: '2', text: '2' },
          { value: '3', text: '3' },
          { value: '4', text: '4' },
          { value: '5', text: '5' },
          { value: '6', text: '6' },
          { value: '7', text: '7' },
          { value: '8', text: '8' },
          { value: '9', text: '9' },
          { value: '10', text: '10' },
          { value: '>10', text: this.$t('Webportal.MoreThan', { value: 10 }) }
      ]
    },
  },

  mounted() {
    this.initializeTheme();

    // Remove status column for non MSM users
    if (!this.isMsmUser)
      _.remove(this.fields, item => { return item.key === 'SurveyInstance.SurveyInstanceStatusId' });

    this.getSurveyUploads();
    this.getSurveyProofUploads();
    this.loadData();

    EventBus.$on('LocalizationChanged', this.localizationChanged);
  },

  beforeUnmount() {
    EventBus.$off('LocalizationChanged', this.localizationChanged);
  },

  methods: {
    msm_date(val) {
      return CustomFilter.msm_date(val)
    },
    msm_datetime(val) {
      return CustomFilter.msm_datetime(val)
    },
    msm_time(val) {
      return CustomFilter.msm_time(val)
    },

    localizationChanged(localizationId) {
      this.getSurveyUploads();
      this.getSurveyProofUploads();
      this.loadDataInternal();
    },

    // Get survey structure data for the structure treeselect
    getSurveyUploads() {
      if (!this.noSurveySelected) {
        SurveysService.getSurveyUploads(this.filter)
          .then(response => {
            if (this.groupUploadQuestions)
              this.uploadItems = _(response.data)
                .flatMap(item => item.children)
                .groupBy(item => item[this.groupUploadQuestions] ?? item.label)
                .map((items, key) => ({
                  id: items.map(item => item.id).join(),
                  label: _.first(items).label,
                  IsPictureUploadQuestion: _.max(items.map(item => item.IsPictureUploadQuestion)),
                  IsVideoUploadQuestion: _.max(items.map(item => item.IsVideoUploadQuestion))
                }))
                .orderBy(item => item.label)
                .value()
            else
              this.uploadItems = response.data;
          })
      }
    },

    getSurveyProofUploads() {
      if (!this.noSurveySelected) {
        SurveysService.getSurveyProofUploads(this.filter)
          .then(response => {
              this.uploadProofItems = response.data;
          })
      }
    },

    getUploadProofsByType(uploadQuestionType) {
      return (this.uploadProofItems.flatMap(survey => survey.children))
        .filter(proof =>
          uploadQuestionType === UploadQuestionType.All ||
          (uploadQuestionType === UploadQuestionType.Standard && !proof.IsPictureUploadQuestion && !proof.IsVideoUploadQuestion) ||
          (uploadQuestionType === UploadQuestionType.Picture && proof.IsPictureUploadQuestion) ||
          (uploadQuestionType === UploadQuestionType.Video && proof.IsVideoUploadQuestion)
        )
    },

    getUploadQuestionsByType(uploadQuestionType) {
      return (this.groupUploadQuestions ? this.uploadItems : this.uploadItems.flatMap(survey => survey.children))
        .filter(question =>
          uploadQuestionType === UploadQuestionType.All ||
          (uploadQuestionType === UploadQuestionType.Standard && !question.IsPictureUploadQuestion && !question.IsVideoUploadQuestion) ||
          (uploadQuestionType === UploadQuestionType.Picture && question.IsPictureUploadQuestion) ||
          (uploadQuestionType === UploadQuestionType.Video && question.IsVideoUploadQuestion)
        )
    },
    selectUploadProofs(uploadQuestionType) {
      this.uploadProofValue =
        this.getUploadProofsByType(uploadQuestionType)
        .map(proof => proof.id);
    },

    selectUploadQuestions(uploadQuestionType) {
      this.uploadValue =
        this.getUploadQuestionsByType(uploadQuestionType)
        .map(question => question.id);
    },

    isUploadProofTypeDisabled(uploadQuestionType) {
      return _.isEmpty(this.getUploadProofsByType(uploadQuestionType));
    },

    isUploadQuestionTypeDisabled(uploadQuestionType) {
      return _.isEmpty(this.getUploadQuestionsByType(uploadQuestionType));
    },

    // Gets survey instances data for the table from the server, including current filter and pagination
    getSurveyInstances() {
      this.toggleSelectionChecked = false;
      this.toggleSelectionIndeterminate = false;

      if (!this.noSurveySelected) {
        this.isLoading = true;

        // Cancel previous request
        if (this.surveyInstancesCancelToken) {
          this.surveyInstancesCancelToken.cancel();
        }

        this.surveyInstancesCancelToken = axios.CancelToken.source();

        SurveysService.getSurveyInstances(this.filter, this.options, this.surveyInstancesCancelToken)
          .then(response => {
            this.totalCount = response.data.TotalCount;
            this.items = response.data.items;
            this.isLoading = false;
          })
          .catch((error) => {
            if (!axios.isCancel(error))
            {
              this.isLoading = false;
              throw error;
            }
          });
      }
      else {
        this.totalCount = 0;
        this.items = [];
      }
    },

    // Applies changed filter and triggeres data loading if necessary
    loadData() {
      // Only load data when effective filter has changed
      // TODO: The order of array elements shouldn't matter when comparing
      if (!_.isEqual(this.filter, this.currentFilter)) {
        let surveyValueChanged = !_.isEqual(this.currentSurveyValue, this.surveyFilter.computedSurveyIds);
        this.currentSurveyValue = this.surveyFilter.computedSurveyIds;
        if (surveyValueChanged)
        {
          this.uploadValue = [];
          this.uploadProofValue = [];
        }
        this.currentFilter = this.filter;

        this.$refs.pagination.resetPaging();

        this.loadDataInternal();
        if (surveyValueChanged)
          this.getSurveyUploads();
          this.getSurveyProofUploads();
      }
    },

    loadDataInternal() {
      this.getSurveyInstances();
    },

    // Called whenever the input of a treeselect control changes
    filterInput(value) {
      // Workaround: Only call data loading when clear all is pressed
      if (_.size(value) === 0)
        this.loadData();
    },

    sortChanged(e) {
      this.sortBy = e.key;
      this.sortDescending = e.order === 'desc' ? true : null;
      this.loadData();
    },

    // Selects a row when clicked
    rowClicked(item) {
      let checked = item.Selected ? !item.Selected : true;

      this.updateToggleSelection(checked);

      item.Selected = checked;
    },

    toggleSelection(event) {
      const checked = event.target.checked;
      _.forEach(this.items, item => {
          item.Selected = checked;
        });
    },

    updateToggleSelection(checked) {
      let count = 0;

      _.forEach(this.items, item => {
          if (item.Selected) count++;
        });

      // New value is not written to property right now
      checked ? count++ : count--;

      // Handle changes in individual flavour checkboxes
      if (count === 0) {
        this.toggleSelectionIndeterminate = false;
        this.toggleSelectionChecked = false;
      } else if (count === this.items.length) {
        this.toggleSelectionIndeterminate = false;
        this.toggleSelectionChecked = true;
      } else {
        this.toggleSelectionIndeterminate = true;
        this.toggleSelectionChecked = false;
      }
    },
    async downloadUploadProofFiles() {
      this.isExporting = true;
      this.downloadProgress = {
        action: this.$t('Webportal.Progress.Initiating').toUpperCase(),
        percentCompleted: 0,
        processedFileSize: null,
        totalFileSize: undefined,
        emptyColor: getComputedStyle(document.body).getPropertyValue('--spinner-bg') || undefined,
        color: getComputedStyle(document.body).getPropertyValue('--spinner-color') || undefined
      };

      try {
        var that = this;

        let response = await SurveysService.getSurveyUploadProofFiles(
          this.selectedSurveyInstanceIds,
          this.uploadProofValue,
          // IMPORTANT: We're using standard (no arrow) function so that "this" points to the actual XMLHttpRequest for accessing the response headers
          function(progressEvent) {
            if (that.downloadProgress.totalFileSize === undefined)
            {
              that.downloadProgress.action = that.$t('Webportal.Progress.Downloaded').toUpperCase();
              that.downloadProgress.totalFileSize = this.getResponseHeader('total-file-size');
            }

            that.downloadProgress.processedFileSize = progressEvent.loaded;
            if (that.downloadProgress.totalFileSize > 0)
              that.downloadProgress.percentCompleted = Math.min(Math.round((progressEvent.loaded * 100) / that.downloadProgress.totalFileSize), 100);
            else
              that.downloadProgress.percentCompleted = 0.1; // ensure visibility of processedFileSize (legend-value)
          });

        if (response.status === 204) {
          this.showToast({
            title: this.$t("Webportal.NoDataFound"),
            props: {
              autoHide: true,
              delay: 5000,
              variant: "warning",
              noCloseButton: true,
            }
          });
        } else {
          this.downloadFile(response);
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.isExporting = false;
      }
    },

    async downloadUploadFiles() {
      this.isExporting = true;
      this.downloadProgress = {
        action: this.$t('Webportal.Progress.Initiating').toUpperCase(),
        percentCompleted: 0,
        processedFileSize: null,
        totalFileSize: undefined,
        emptyColor: getComputedStyle(document.body).getPropertyValue('--spinner-bg') || undefined,
        color: getComputedStyle(document.body).getPropertyValue('--spinner-color') || undefined
      };

      try {
        var that = this;

        let response = await SurveysService.getSurveyUploadFiles(
        this.selectedSurveyInstanceIds,
        this.groupUploadQuestions ? _.flatMap(this.uploadValue, item => item.split(',').map(s => parseInt(s))) : this.uploadValue,
        // IMPORTANT: We're using standard (no arrow) function so that "this" points to the actual XMLHttpRequest for accessing the response headers
        function(progressEvent) {
          if (that.downloadProgress.totalFileSize === undefined)
          {
            that.downloadProgress.action = that.$t('Webportal.Progress.Downloaded').toUpperCase();
            that.downloadProgress.totalFileSize = this.getResponseHeader('total-file-size');
          }

          that.downloadProgress.processedFileSize = progressEvent.loaded;
          if (that.downloadProgress.totalFileSize > 0)
            that.downloadProgress.percentCompleted = Math.min(Math.round((progressEvent.loaded * 100) / that.downloadProgress.totalFileSize), 100);
          else
            that.downloadProgress.percentCompleted = 0.1; // ensure visibility of processedFileSize (legend-value)
        });

        if (response.status === 204) {
          this.showToast({
            title: this.$t("Webportal.NoDataFound"),
            props: {
              autoHide: true,
              delay: 5000,
              variant: "warning",
              noCloseButton: true,
            }
          });
        } else {
          this.downloadFile(response);
        }

      } catch (error) {
        console.error(error);
      } finally {
        this.isExporting = false;
      }
    },

    showPreview(id) {
      this.$refs.videoControl.pause();

      if (this.previewCancelToken)
        this.previewCancelToken.cancel('showPreview');

      this.previewCancelToken = axios.CancelToken.source();

      this.isPreviewLoading = true;
      this.isPreviewVisible = true;

      MediaService.getVideoToken(id, this.previewCancelToken)
        .then((response) => {
          this.isPreviewLoading = false;

          this.previewUrl =`${process.env.BASE_URL}api/media/stream?token=${response.data.token}`;
          this.$refs.videoControl.load();
          this.$refs.videoControl.play();
        })
        .catch((thrown) => {
          if (thrown.message != 'showPreview')
          {
            this.previewUrl = null;
            this.isPreviewLoading = false;
          }

          if (axios.isCancel(thrown)) {
          }
        });
    },

    closePreview() {
      this.$refs.videoControl.pause();
      this.isPreviewLoading = false;
      this.isPreviewVisible = false;
      this.previewUrl = null;

      if (this.previewCancelToken)
        this.previewCancelToken.cancel('closePreview');
    },

    calendarWeekAndDateFormatted(item) {
      if (!item.SurveyInstance.TimeStampSurveyStart)
        return '';

      if (this.isMsmUser || (item.ShowCheckDate && item.ShowCheckTime))
        return item.SurveyInstance.CalendarWeek + ' (' + this.msm_datetime(item.SurveyInstance.TimeStampSurveyStart) + ')';
      else if (item.ShowCheckDate)
        return item.SurveyInstance.CalendarWeek + ' (' + this.msm_date(item.SurveyInstance.TimeStampSurveyStart) + ')';
      else if (item.ShowCheckTime)
        return item.SurveyInstance.CalendarWeek + ' (' + this.msm_time(item.SurveyInstance.TimeStampSurveyStart) + ')';
      else
        return item.SurveyInstance.CalendarWeek
    }
  }
};
</script>

<style lang="scss">
.treeselect_upload .vue3-treeselect__control {
  border-radius: 5px 0px 0px 5px !important;
  height: 2.5rem;
}
.treeselect_upload_btn {
  border-radius: 0px 5px 5px 0px !important;
  height: 2.5rem;
}
.media-explorer-toolbar {
  .page-link,
  .btn {
    height: 2.5rem;
    display: flex;
    align-items: center;
  }
}
</style>
